import React, { useState } from 'react';
import { GIButton, Modal } from 'app-components';
import styled from 'styled-components';
import { ModalContextType } from '../../../../contexts/modalContext';
import useApiHandling from '../../hooks/useApiHandling';
import { MaskedInput, DATE_MASK, IGetSepDetails, CsrOptionName, ValidationErrorMessage } from '../../utils';
import { isDateValid } from '../../../../utils/dates/date.utils';
import { MM_SLASH_DD_SLASH_YYYY } from '../../../../utils/dates/dateFormats.utils';

const StyledField = styled.div`
    .date-picker,
    .shown.error--msg {
        margin-top: 4px !important;
        padding: 8px;
        background-color: ${({ theme }) => theme.dangerLightest};
    }
    @media (max-width: 768px) {
        flex-direction: column;
        justify-content: center;
    }

    .gi-input {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        text-align: left;
    }

    .gi-input__label {
        margin-left: -10px;
    }

    @media (max-width: 768px) {
        .gi-input__label {
            margin-left: 0px;
        }
    }
`;

const StyledModalBody = styled.div`
    .gi-input__input {
        width: 115px;
    }

    @media (max-width: 768px) {
        .gi-modal__body {
            max-height: 0%;
            padding-bottom: 20px;
        }
    }
`;

const StyledControls = styled.div`
    display: flex;
    gap: 10px;
    justify-content: flex-end;

    @media (max-width: 768px) {
        margin-top: 0;
        flex-direction: column;
        gap: 5px;
    }
`;

interface IModalProps extends ModalContextType {
    sepDetails: IGetSepDetails;
}

const ChangeOpenSpecialEnrollment = ({ closeModal, sepDetails }: IModalProps) => {
    const [startDate, setStartDate] = useState<string>(sepDetails.sepStartDate);
    const [endDate, setEndDate] = useState<string>(sepDetails.sepEndDate);
    const [startDateErrorMsg, setStartDateErrorMsg] = useState<string | null>(null);
    const [endDateErrorMsg, setEndDateErrorMsg] = useState<string | null>(null);

    const { handleSaveSepDetails } = useApiHandling();

    function onClose() {
        closeModal('changeOpenSpecialEnrollment');
    }

    function onSave() {
        const updatedSepDetails = {
            ...sepDetails,
            sepStartDate: startDate,
            sepEndDate: endDate,
        };
        handleSaveSepDetails(updatedSepDetails, CsrOptionName.SPECIAL_ENROLLMENT);
    }

    const handleStartDateChange = (date: string) => {
        if (isDateValid(date, MM_SLASH_DD_SLASH_YYYY)) {
            setStartDate(date);
            setStartDateErrorMsg(null);
        } else {
            setStartDateErrorMsg('cap.applications.modal.csrspedateerror');
        }
    };

    const handleEndDateChange = (date: string) => {
        if (isDateValid(date, MM_SLASH_DD_SLASH_YYYY)) {
            setEndDate(date);
            setEndDateErrorMsg(null);
        } else {
            setEndDateErrorMsg('cap.applications.modal.csrspedateerror');
        }
    };

    return (
        <Modal id="change_open_special_enrollment">
            <Modal.Header
                id="change_open_special_enrollment_title"
                titleId="cap.applications.modal.openspecialenorollment2"
                onHide={onClose}
            />
            <StyledModalBody>
                <Modal.Body>
                    <StyledField>
                        <MaskedInput
                            label={{
                                labelText: 'cap.application.modal.csrenterspestart',
                                labelClasses: [''],
                                wrapperClasses: [''],
                                isRequired: false,
                            }}
                            input={{
                                id: 'ose_start_date',
                                name: 'startDate',
                                placeholder: 'mm/dd/yyyy',
                                type: 'text',
                                isInputRequired: true,
                                value: startDate,
                                mask: DATE_MASK,
                            }}
                            actions={{
                                handleFieldChange: (date: string) => {
                                    setStartDate(date);
                                },
                                handleFieldBlur: (date: string) => handleStartDateChange(date),
                            }}
                        />
                        <ValidationErrorMessage
                            isVisible={startDateErrorMsg !== null}
                            messageText={startDateErrorMsg}
                        />

                        <MaskedInput
                            label={{
                                labelText: 'cap.application.modal.csrenterspeend',
                                labelClasses: [''],
                                wrapperClasses: [''],
                                isRequired: false,
                            }}
                            input={{
                                id: 'ose_end_date',
                                name: 'endDate',
                                placeholder: 'mm/dd/yyyy',
                                type: 'text',
                                isInputRequired: true,
                                value: endDate,
                                mask: DATE_MASK,
                            }}
                            actions={{
                                handleFieldChange: (date: string) => {
                                    setEndDate(date);
                                },
                                handleFieldBlur: (date: string) => handleEndDateChange(date),
                            }}
                        />
                        <ValidationErrorMessage isVisible={endDateErrorMsg !== null} messageText={endDateErrorMsg} />
                    </StyledField>
                </Modal.Body>
            </StyledModalBody>
            <Modal.Controls>
                <StyledControls>
                    <GIButton
                        id="change_open_special_enrollment_continue_button"
                        onClickHandler={onSave}
                        isDisabled={startDateErrorMsg !== null || endDateErrorMsg !== null}
                        variant="primary"
                        labelId="button.continue"
                    />
                    <GIButton id="change_open_special_enrollment_cancel_button" 
                        onClickHandler={onClose} 
                        variant="secondary" 
                        labelId="button.cancel" 
                    />
                </StyledControls>
            </Modal.Controls>
        </Modal>
    );
};

export default ChangeOpenSpecialEnrollment;
